import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PortalHeader from '../../components/Portal/PortalHeader';
import TestModeBanner from '../../components/TestMode/TestModeBanner';
import CalendarTab from './Scheduling/CalendarTab';
import SlotEditorTab from './Scheduling/SlotEditorTab';
import RegistrationTab from './Scheduling/RegistrationTab';
import SubscribeTab from './Scheduling/SubscribeTab';
// Shared season-dates view (also the customer /portal/customer/calendar page).
// Read-only; renders the season canon (recitals + closures) for staff here.
import SeasonCalendar from '../CustomerPortal/SeasonCalendar';
import '../../components/Portal/portalSurface.css';
import './Scheduling/Scheduling.css';

// Unified scheduling surface — replaces the standalone timeSlots pages
// (ScheduleCalendar / ManageTimeSlots / GenerateTestTimeSlots). Same `timeSlots`
// collection and document shape; the tabs each own their data fetch + write
// paths, ported verbatim from the legacy pages (UI relocated, not
// re-implemented). The legacy routes redirect here with ?tab=… preselected.
// The Visual drag-grid tab was retired once the Calendar tab became directly
// editable (its open-slot/swap job folded into the Calendar drawer).
const TABS = [
	{ key: 'calendar', label: 'Calendar', icon: 'fa-calendar' },
	{ key: 'editor', label: 'Slot Editor', icon: 'fa-calendar-pen' },
	// Fall registration (Track 1 request-and-assign): open-slot pool +
	// incoming family requests + assign. Admin/owner surface (the
	// registrationRequests read rule); others see an access notice.
	{ key: 'registration', label: 'Registration', icon: 'fa-clipboard-list' },
	// Read-only season dates (recitals + studio closures) from the season canon
	// — the same view families see at /portal/customer/calendar. Staff can't
	// reach the customer-only route, so it's mirrored here.
	{ key: 'important-dates', label: 'Important Dates', icon: 'fa-calendar-star' },
	// Self-service ICS subscription links (one per location, so each can carry a
	// distinct colour in the staff member's own calendar app). Lives HERE rather
	// than the Admin panel because AdminPanel gates on role === 'admin' and
	// Annelise is 'employee' — the admin surface would have excluded one of the
	// three people who need it.
	{ key: 'subscribe', label: 'Subscribe', icon: 'fa-square-rss' },
];

const VALID = TABS.map((t) => t.key);

const Scheduling = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const initial = searchParams.get('tab');
	const [activeTab, setActiveTab] = useState(
		VALID.includes(initial) ? initial : 'calendar'
	);

	const selectTab = (key) => {
		setActiveTab(key);
		// Keep the URL in sync so a tab is shareable / survives refresh.
		const next = new URLSearchParams(searchParams);
		next.set('tab', key);
		setSearchParams(next, { replace: true });
	};

	return (
		<>
			<PortalHeader
				title="Scheduling"
				subtitle="Calendar and slot editor — one place for weekly time slots"
			/>
			<TestModeBanner />
			<div className="ps-page scheduling-page">
				<div className="ps-tabs" role="tablist">
					{TABS.map((t) => (
						<button
							key={t.key}
							type="button"
							role="tab"
							aria-selected={activeTab === t.key}
							className={`ps-tab${activeTab === t.key ? ' is-active' : ''}`}
							onClick={() => selectTab(t.key)}>
							<i className={`fa-sharp fa-solid ${t.icon}`} />
							{t.label}
						</button>
					))}
				</div>

				{activeTab === 'calendar' && <CalendarTab />}
				{activeTab === 'editor' && <SlotEditorTab />}
				{activeTab === 'registration' && <RegistrationTab />}
				{/* `embedded` suppresses the page's own <h1> and outer column
				    padding — this tab already draws a PortalHeader above, so the
				    unembedded page rendered a second heading inside it. */}
				{activeTab === 'important-dates' && <SeasonCalendar showFlex embedded />}
				{activeTab === 'subscribe' && <SubscribeTab />}
			</div>
		</>
	);
};

export default Scheduling;
